<template>
  <section class="funnel">
    <Transition name="step" mode="out-in">
      <!-- Step 1: plan + number -->
      <div v-if="step === Steps.Plan" key="plan" class="step">
        <p class="eyebrow">{{ t('tagline') }}</p>
        <h1 class="title">{{ t('heroTitle') }}</h1>
        <p class="lede">{{ t('heroText') }}</p>

        <div class="plans-head">
          <h2 class="section-title">{{ t('plansTitle') }}</h2>
          <span class="badge">{{ t('trialBadge') }}</span>
        </div>
        <PlanPicker :tiers="brandConfig.prices" :selected="selectedTier" :currency="brandConfig.currency" @select="selectTier" />

        <template v-if="alreadySubscribed">
          <p class="notice notice--success">{{ t('alreadySubscribed') }}</p>
          <a class="btn btn-primary" :href="portalHref(brandConfig)">{{ t('portalCta') }}</a>
        </template>

        <template v-else>
          <template v-if="!headerEnriched">
            <label class="label" for="msisdn">{{ t('mobileLabel') }}</label>
            <div class="field msisdn-field" @click="msisdnInputEl?.focus()">
              <span class="msisdn-field__prefix" aria-hidden="true">+{{ brandConfig.msisdn.countryCode }}</span>
              <input
                id="msisdn"
                ref="msisdnInputEl"
                :value="msisdnLocal"
                class="msisdn-field__input"
                type="tel"
                inputmode="numeric"
                autocomplete="tel-national"
                :placeholder="t('mobilePlaceholder')"
                @input="onMsisdnInput"
                @keyup.enter="onContinue"
              />
            </div>
            <p class="hint">{{ t('mobileHint') }}</p>
          </template>
          <p v-else class="he-note">
            <span class="he-note__dot" aria-hidden="true"></span>
            {{ displayMsisdn }}
          </p>

          <button id="continue_button" class="btn btn-primary" :class="{ 'btn--busy': busy }" :disabled="busy" @click="onContinue">
            {{ headerEnriched ? t('subscribeCta') : t('continueCta') }}
          </button>
          <p v-if="errorKey" class="error" role="alert">{{ t(errorKey) }}</p>
        </template>
      </div>

      <!-- Step 2: OTP (typed numbers only). Verified automatically on the 4th digit; no submit button. -->
      <div v-else-if="step === Steps.Otp" key="otp" class="step step--center">
        <h1 class="title">{{ t('otpTitle') }}</h1>
        <p class="lede">{{ t('otpText') }} <strong class="nowrap">{{ displayMsisdn }}</strong></p>

        <OtpInput v-model="otp" :length="4" class="otp-input" @complete="onVerify" />
        <p v-if="errorKey" class="error" role="alert">{{ t(errorKey) }}</p>
        <p v-else class="otp-status" :class="{ 'otp-status--busy': busy }" aria-live="polite">
          {{ busy ? t('otpVerifying') : t('otpAutoHint') }}
        </p>

        <div class="otp-actions">
          <button type="button" class="link" :disabled="resendIn > 0 || busy" @click="onResend">
            {{ resendIn > 0 ? t('otpResendIn', { s: resendIn }) : t('otpResend') }}
          </button>
          <span class="otp-actions__sep" aria-hidden="true">·</span>
          <button type="button" class="link" @click="changeNumber">{{ t('otpChangeNumber') }}</button>
        </div>
      </div>

      <!-- Step 3: confirmation (double opt-in, Evina-protected button) -->
      <div v-else key="confirm" class="step">
        <h1 class="title">{{ t('confirmTitle') }}</h1>

        <dl class="summary">
          <div class="summary__row">
            <dt>{{ t('confirmPlan') }}</dt>
            <dd>{{ t(selectedTier.labelKey) }}</dd>
          </div>
          <div class="summary__row">
            <dt>{{ t('confirmNumber') }}</dt>
            <dd class="nowrap">{{ displayMsisdn }}</dd>
          </div>
          <div class="summary__row">
            <dt>{{ t('confirmPrice') }}</dt>
            <dd><span class="summary__price">{{ price }}</span> {{ t(selectedTier.periodKey) }}</dd>
          </div>
        </dl>

        <div class="trial">
          <p class="trial__title">{{ t('confirmTrial') }}</p>
          <p class="trial__text">{{ t('confirmChargeFrom', { date: chargeDate, price, period: t(selectedTier.periodKey) }) }}</p>
          <p class="trial__note">{{ t('confirmRenewal') }}</p>
        </div>

        <!-- Enabled once the OTP is verified and the Evina kit attempt has settled, so the tap is observed. -->
        <button
          id="subscribe_button"
          class="btn btn-primary evina-protect"
          :class="{ 'btn--busy': busy }"
          :disabled="busy || !evina.settled"
          @click="onConfirm"
        >
          {{ t('confirmCta') }}
        </button>
        <p v-if="errorKey" class="error" role="alert">{{ t(errorKey) }}</p>

        <p class="consent">
          {{ t('confirmConsent') }}
          <a class="text-link" :href="brandConfig.termsConditionsUrl" target="_blank" rel="noopener noreferrer">{{ t('termsConditions') }}</a>
        </p>
        <button type="button" class="link link--block" @click="changeNumber">{{ t('otpChangeNumber') }}</button>
      </div>
    </Transition>
  </section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useBrandStore } from '@/stores/brandStore'
import { useSubscriptionStore, Steps } from '@/stores/subscriptionStore'
import { formatPrice, formatMsisdnDisplay, formatChargeDate } from '@/functions/format'
import PlanPicker from '@/components/PlanPicker.vue'
import OtpInput from '@/components/OtpInput.vue'

const brandStore = useBrandStore()
const { brandConfig, locale } = storeToRefs(brandStore)
const { t } = brandStore

const subscriptionStore = useSubscriptionStore()
const { msisdn, headerEnriched, step, selectedTier, otp, otpSentAt, busy, alreadySubscribed, evina } = storeToRefs(subscriptionStore)
const { selectTier, continueWithNumber, verifyOtp, requestOtp, armEvina, subscribe, changeNumber, checkSubscription, portalHref } = subscriptionStore

const route = useRoute()
const router = useRouter()

const errorKey = ref('')
const msisdnInputEl = ref(null)
const resendIn = ref(0)
let resendTimer = null

const RESEND_COOLDOWN_S = 30
// Gives Evina's click beacon time to leave before the subscribe call and the navigation.
const EVINA_CLICK_GRACE_MS = 300

const displayMsisdn = computed(() => formatMsisdnDisplay(msisdn.value, brandConfig.value.msisdn.countryCode))
const price = computed(() => formatPrice(selectedTier.value?.amount, brandConfig.value.currency, locale.value))
const chargeDate = computed(() => formatChargeDate(brandConfig.value.trialDays ?? 0, locale.value))

// The country code is a fixed prefix in the box; the input holds only the local part while the
// store keeps the full international form.
const msisdnLocal = computed(() => {
  const digits = String(msisdn.value || '').replace(/\D/g, '')
  const code = brandConfig.value.msisdn.countryCode
  return code && digits.startsWith(code) ? digits.slice(code.length) : digits
})

function onMsisdnInput(event) {
  const { countryCode, trunkPrefix, localLength } = brandConfig.value.msisdn
  let local = event.target.value.replace(/\D/g, '')
  if (countryCode && local.startsWith(countryCode) && local.length > (localLength || 0)) local = local.slice(countryCode.length)
  if (countryCode && trunkPrefix && local.startsWith(trunkPrefix)) local = local.slice(trunkPrefix.length)
  if (localLength) local = local.slice(0, localLength)
  msisdn.value = local ? `${countryCode}${local}` : ''
  if (event.target.value !== local) event.target.value = local
}

onMounted(() => {
  if (!selectedTier.value) selectTier(brandConfig.value.prices?.[0] ?? null)
})

// HE visitor: MTN identified the SIM, so check with MADAPI before they tap anything and send an
// existing subscriber straight to the portal.
watch(
  headerEnriched,
  async (enriched) => {
    if (!enriched || !msisdn.value) return
    if (await checkSubscription(brandConfig.value, msisdn.value)) window.location.assign(portalHref(brandConfig.value))
  },
  { immediate: true }
)

watch(step, () => {
  errorKey.value = ''
  if (step.value === Steps.Otp) startResendCooldown()
  if (step.value === Steps.Confirm) nextTick(arm)
})

watch(otpSentAt, startResendCooldown)

function startResendCooldown() {
  clearInterval(resendTimer)
  resendIn.value = RESEND_COOLDOWN_S
  resendTimer = setInterval(() => {
    resendIn.value -= 1
    if (resendIn.value <= 0) clearInterval(resendTimer)
  }, 1000)
}

onBeforeUnmount(() => clearInterval(resendTimer))

// The kit was issued at the first click; re-arming binds it to the freshly rendered Confirm button.
function arm() {
  armEvina(brandConfig.value)
}

async function onContinue() {
  errorKey.value = ''
  const { ok, reason } = await continueWithNumber(brandConfig.value)
  if (ok) return
  if (reason === 'alreadySubscribed') return
  errorKey.value = reason || 'subscribeError'
  if (reason === 'invalidNumber') msisdnInputEl.value?.focus()
}

async function onVerify() {
  if (otp.value.length < 4 || busy.value) return
  errorKey.value = ''
  const { ok, reason } = await verifyOtp(brandConfig.value, otp.value)
  if (!ok) {
    errorKey.value = reason || 'otpError'
    otp.value = ''
  }
}

async function onResend() {
  errorKey.value = ''
  const { ok, reason } = await requestOtp(brandConfig.value)
  if (!ok) errorKey.value = reason || 'otpSendError'
}

function onConfirm() {
  if (busy.value) return
  errorKey.value = ''
  setTimeout(async () => {
    const { ok } = await subscribe(brandConfig.value)
    router.push({ name: ok ? 'thank-you' : 'error', query: route.query })
  }, EVINA_CLICK_GRACE_MS)
}
</script>

<style scoped>
.funnel {
  padding-top: var(--spacing-md);
}

.step {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.step--center {
  text-align: center;
  align-items: center;
}

.eyebrow {
  font-family: var(--font-display);
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--color-primary);
}

.title {
  font-family: var(--font-display);
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: 0.01em;
  text-wrap: balance;
}

.lede {
  font-size: 0.95rem;
  line-height: 1.6;
  color: var(--color-text-secondary);
  max-width: 36ch;
}

.step--center .lede {
  max-width: 30ch;
}

.plans-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-sm);
}

.section-title {
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
}

.label {
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  margin-top: var(--spacing-sm);
}

.msisdn-field {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  text-align: left;
  cursor: text;
}

.msisdn-field:focus-within {
  border-color: var(--color-primary);
}

.msisdn-field__prefix {
  font-family: var(--font-display);
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-text-secondary);
}

.msisdn-field__input {
  flex: 1;
  min-width: 0;
  background: transparent;
  color: var(--color-text);
  font-family: var(--font-display);
  font-size: 1.35rem;
  font-weight: 700;
  letter-spacing: 0.06em;
}

.msisdn-field__input::placeholder {
  color: var(--color-text-secondary);
  opacity: 0.6;
  font-weight: 600;
}

.hint {
  font-size: 0.8rem;
  color: var(--color-text-secondary);
  margin-top: calc(-1 * var(--spacing-sm));
}

.he-note {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  align-self: flex-start;
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: 999px;
  background: var(--color-surface);
  border: 1px solid var(--color-surface-border);
  font-family: var(--font-display);
  font-size: 1.1rem;
  font-weight: 700;
}

.he-note__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-success);
  box-shadow: 0 0 0 4px rgba(61, 220, 151, 0.18);
}

.otp-input {
  margin: var(--spacing-sm) 0;
}

.otp-status {
  font-size: 0.85rem;
  color: var(--color-text-secondary);
}

.otp-status--busy {
  color: var(--color-accent);
  font-weight: 600;
}

.otp-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  font-size: 0.9rem;
}

.otp-actions__sep {
  color: var(--color-text-secondary);
}

.summary {
  display: flex;
  flex-direction: column;
  background: var(--color-surface);
  border: 1px solid var(--color-surface-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.summary__row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
}

.summary__row + .summary__row {
  border-top: 1px solid var(--color-surface-border);
}

.summary dt {
  font-size: 0.8rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
}

.summary dd {
  font-weight: 600;
  text-align: right;
}

.summary__price {
  font-family: var(--font-display);
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--color-primary);
}

.trial {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: var(--spacing-md);
  border-radius: var(--radius-lg);
  border: 1px solid rgba(255, 203, 5, 0.35);
  background: linear-gradient(135deg, rgba(255, 203, 5, 0.12), rgba(255, 203, 5, 0.03));
}

.trial__title {
  font-family: var(--font-display);
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--color-primary);
}

.trial__text {
  font-size: 0.92rem;
  line-height: 1.55;
}

.trial__note {
  font-size: 0.8rem;
  color: var(--color-text-secondary);
}

.consent {
  text-align: center;
  font-size: 0.8rem;
  color: var(--color-text-secondary);
}

.notice {
  padding: var(--spacing-md);
  border-radius: var(--radius-lg);
  font-weight: 600;
  text-align: center;
}

.notice--success {
  background: rgba(61, 220, 151, 0.12);
  border: 1px solid rgba(61, 220, 151, 0.4);
}

.error {
  text-align: center;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--color-error);
}

.nowrap {
  white-space: nowrap;
}

/* Step transition: a short fade + lift, disabled for reduced motion in main.css. */
.step-enter-active,
.step-leave-active {
  transition: opacity 180ms ease, transform 180ms ease;
}

.step-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.step-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
